import type { KycMapping, KycMappingDoc, KycMappingSection, KycState } from '../../types'

/**
 * Which composition `/apply` renders for the caller's review state. The BFF
 * already decides *which documents* appear (panel and field conditionals are
 * rewritten server-side); this decides whether the form is worth showing at all.
 *
 *   todo     → the form, as normal
 *   waiting  → everything actionable is submitted; nothing to do but wait
 *   failed   → staff rejected a document: surface the reason, then the form
 *   complete → KYC verified; nothing further, ever
 */
export type KycStepState = 'todo' | 'waiting' | 'failed' | 'complete'

/** A rejected document, as shown in the failure dialog. */
export interface FailedDoc {
  docKey: string
  label: string
  failReason: string | null
}

export interface KycStep {
  state: KycStepState
  /** Populated only when `state === 'failed'`. */
  failedDocs: FailedDoc[]
}

/**
 * Derive the step from the caller's KYC state. Pure — no React, no fetching —
 * so it can be checked directly against a captured /me/kyc-form payload.
 *
 * Prefers the complete, ordered `kyc.mapping`; falls back to the legacy
 * `kyc.sections` shape for responses (or fixtures) captured before mapping
 * shipped. Both paths implement the same decision tree — see deriveFromMapping
 * for the rationale that governs which state wins.
 */
export function deriveKycStep(kyc?: KycState): KycStep {
  if (!kyc) return { state: 'todo', failedDocs: [] }
  if (kyc.mapping) return deriveFromMapping(kyc.mapping)
  return deriveFromSections(kyc)
}

/**
 * Mapping-driven derivation. Sections and documents are walked in their declared
 * order (`sectionOrder` / `documentOrder`) — object key order is not the
 * contract.
 *
 * Everything keys off `uploadAllowed`, the BFF's authoritative "this customer
 * can act on this document now" flag: true for a `pending` doc in an open
 * section and for any rejected doc awaiting its replacement, false for anything
 * locked, already approved, or sitting under review. Deriving actionability from
 * it (rather than from section.open + status) is what keeps this in step with
 * the server's own view of what the customer may do.
 *
 * A rejection the customer can fix always wins, even while a sibling in the same
 * section is still `submitted`. An earlier version held failures back until the
 * whole review batch settled, on the theory that the customer shouldn't be
 * pulled into a re-upload they might have to repeat — but that stranded them on
 * a "waiting" screen with no way to fix a document the BFF had explicitly
 * reopened for upload. Surfacing it immediately matches `recommendedAction:
 * "reupload"` and the handoff guide's own reference flow.
 *
 * A doc counts whether or not it's `required`, so the form stays reachable while
 * optional uploads remain outstanding. A `failed` doc with `uploadAllowed:
 * false` is deliberately *not* surfaced — there'd be nothing the customer could
 * do about it — and reads as `waiting`.
 */
function deriveFromMapping(mapping: KycMapping): KycStep {
  if (mapping.caseStatus === 'verified') return { state: 'complete', failedDocs: [] }

  const openSections = mapping.sectionOrder
    .map((sectionKey) => mapping.sections[sectionKey])
    .filter((section): section is KycMappingSection => Boolean(section && (section.open || section.status === 'open')))

  const openDocs = openSections.flatMap((section) =>
    section.documentOrder
      .map((docKey) => section.documents[docKey])
      .filter((doc): doc is KycMappingDoc => Boolean(doc)),
  )

  const failedDocs: FailedDoc[] = openDocs
    .filter((doc) => doc.status === 'failed' && doc.uploadAllowed)
    .map((doc) => ({ docKey: doc.key, label: doc.label, failReason: doc.failReason }))

  // A fixable rejection outranks everything — the reason has to reach them.
  if (failedDocs.length > 0) return { state: 'failed', failedDocs }

  // If any open document still needs an initial upload, keep form actionable
  const anyPending = openDocs.some((doc) => doc.status === 'pending' || !doc.hasUpload)
  if (anyPending) return { state: 'todo', failedDocs: [] }

  // An empty list (including the local schema-only fallback) is not evidence
  // of submission. Check closed sections too, since a reviewed batch may close
  // before the next section opens. The schema still controls upload visibility.
  const hasSubmittedDocuments = Object.values(mapping.sections).some((section) =>
    Object.values(section.documents).some((doc) =>
      ['submitted', 'passed', 'failed'].includes(doc.status),
    ),
  )
  return { state: hasSubmittedDocuments ? 'waiting' : 'todo', failedDocs: [] }
}

/**
 * Legacy derivation from the section-oriented `kyc.sections` shape. Same
 * decision tree as deriveFromMapping, but sourced from the older payload, which
 * has neither declared ordering (so object key order is used) nor
 * `uploadAllowed` — actionability is inferred from an open section instead, and
 * a `failed` doc there is taken as re-uploadable, which the handoff guide
 * documents it always is. Retained for pre-mapping responses; remove once the
 * BFF always sends `mapping`.
 */
function deriveFromSections(kyc: KycState): KycStep {
  if (kyc.kycStatus === 'verified') return { state: 'complete', failedDocs: [] }

  const openDocs = Object.values(kyc.sections ?? {})
    .filter((section) => section?.open)
    .flatMap((section) => Object.entries(section.docs ?? {}))

  const anyPending = openDocs.some(([, doc]) => doc?.status === 'pending')

  const failedDocs = openDocs
    .filter(([, doc]) => doc?.status === 'failed')
    .map(([docKey, doc]) => ({ docKey, label: doc.label, failReason: doc.failReason }))

  if (failedDocs.length > 0) return { state: 'failed', failedDocs }
  if (anyPending) return { state: 'todo', failedDocs: [] }
  const hasSubmittedDocuments = Object.values(kyc.sections ?? {}).some((section) =>
    Object.values(section.docs ?? {}).some((doc) =>
      doc && ['submitted', 'passed', 'failed'].includes(doc.status),
    ),
  )
  return { state: hasSubmittedDocuments ? 'waiting' : 'todo', failedDocs: [] }
}
